import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';
import { ApiServiceService } from '../../services/common/api-service.service';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {
  lookingFor = 'Women';
  ageFrom = 21;
  ageTo = 35;
  religion = 'Any Religion';
  location = '';

  // Master lists
  religions: any[] = [];
  citys: any[] = [];
  castes: any[] = [];
  occupations: any[] = [];

  // Search results states
  searched = false;
  loadingMatches = false;
  filteredMatches: any[] = [];
  base64Images: { [key: string]: string } = {};
  gender = '';

  constructor(
    private router: Router, 
    private authService: AuthService,
    private apiService: ApiServiceService
  ) {}

  get isLoggedIn(): boolean {
    return this.authService.isLoggedIn();
  }

  ngOnInit(): void {
    if (this.isLoggedIn) {
      this.router.navigate(['/dashboard']);
      return;
    }

    // Fetch master lists for dropdown resolving
    forkJoin([
      this.apiService.getReligions(),
      this.apiService.getCity(),
      this.apiService.getCastes(),
      this.apiService.getOccupation()
    ]).subscribe(
      ([religionsRes, citysRes, castesRes, occupationsRes]) => {
        this.religions = religionsRes.data || [];
        this.citys = citysRes.data || [];
        this.castes = castesRes.data || [];
        this.occupations = occupationsRes.data || [];
      },
      (error) => {
        console.error('Error loading master data in HomeComponent:', error);
      }
    );
  }

  onSearch(event: Event) {
    event.preventDefault();
    this.searched = true;
    this.loadingMatches = true;

    const genderVal = this.lookingFor === 'Women' ? 'Female' : (this.lookingFor === 'Men' ? 'Male' : '');
    this.gender = genderVal;

    // Resolve religion ID
    let relId = '';
    if (this.religion && this.religion !== 'Any Religion') {
      const match = this.religions.find(r => (r.religion_nme || r.religion_name || '').toLowerCase() === this.religion.toLowerCase());
      if (match) relId = match._id;
    }

    // Resolve city ID
    let cityId = '';
    if (this.location) {
      const match = this.citys.find(c => (c.city_nme || c.city_name || '').toLowerCase().includes(this.location.toLowerCase()));
      if (match) cityId = match._id;
    }

    const sessionToken = localStorage.getItem('sessionToken') || '';
    const profileId = localStorage.getItem('profileId') || '000000000000000000000000';

    const payload = {
      action: 'find_all',
      type: 'filter',
      profileId: profileId,
      data: {
        filter_name: '',
        horoscope_filter: { rasi_id: '', natchr_id: '', gothrm_id: '', dosham: '' },
        location_filter: { city_id: cityId, dist_id: '', state_id: '', country_id: '', location_proximity: '' },
        education_filter: { education_id: [], higher_education_id: [] },
        profession_filter: { occupation_id: [], employmentype_id: [] },
        lifestyle_and_food_filter: { smoking: '', drinking: '', food: '' },
        religious_filter: { religion_id: relId, caste_id: '', sub_caste_id: '' },
        personal_filter: {
          gender: this.gender,
          age_from: Number(this.ageFrom),
          age_to: Number(this.ageTo),
          height_from: '', height_to: '',
          weight_from: '', weight_to: '',
          salary_from: '', salary_to: '',
          language_id: '', citizenship_id: '', physical_status: '', family_type: '', marital_status: ''
        }
      }
    };

    this.apiService.getfilterMatches(sessionToken, payload).subscribe(
      response => {
        if (response.status === 'success' && Array.isArray(response.data)) {
          this.filteredMatches = response.data;
          this.base64Images = {};
          this.filteredMatches.forEach(profile => {
            const idKey = profile.basic_info?.keycloak_id || profile.basic_info?.ory_identity_id || profile._id;
            if (idKey && profile.personal_info?.avatar_Id) {
              const imgUrl = `${idKey}/gallery/avatar/${profile.personal_info.avatar_Id}`;
              this.loadImage(imgUrl, profile._id);
            }
          });
        } else {
          this.filteredMatches = [];
        }
        this.loadingMatches = false;
      },
      error => {
        console.error('Error fetching guest search results:', error);
        this.filteredMatches = [];
        this.loadingMatches = false;
      }
    );
  }

  loadImage(imagePath: string, profileId: string): void {
    if (this.base64Images[profileId]) return;
    this.apiService.getImageBase64(imagePath).subscribe(
      (data) => {
        if (data && data.base64Image) {
          this.base64Images[profileId] = data.base64Image;
        }
      },
      (error) => {
        console.error('Error loading image base64:', error);
      }
    );
  }

  getMaskedName(profile: any): string {
    if (this.isLoggedIn) {
      return `${profile.basic_info?.first_name || ''} ${profile.basic_info?.last_name || ''}`.trim();
    }
    const firstName = profile.basic_info?.first_name || '';
    const lastName = profile.basic_info?.last_name || '';
    const lastInitial = lastName ? lastName.charAt(0) + '.' : '';
    return `${firstName} ${lastInitial}`.trim() || 'Profile';
  }

  redirectToSignup() {
    Swal.fire({
      title: 'Sign Up Required',
      text: 'Please sign up or sign in to view complete profile details, shortlist, and chat.',
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Join Free',
      cancelButtonText: 'Sign In',
      confirmButtonColor: '#800020',
      cancelButtonColor: '#C5A059'
    }).then((result) => {
      if (result.isConfirmed) {
        this.router.navigate(['/signup-basic']);
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        this.router.navigate(['/login']);
      }
    });
  }

  getAge(dateOfBirth: string): number {
    if (!dateOfBirth) return 0;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  getCasteName(profile: any): string {
    const caste = profile.religion_info?.caste_id;
    if (!caste) return '';
    if (typeof caste === 'object') return caste.caste_nme || '';
    const found = this.castes.find(c => c._id === caste);
    return found ? found.caste_nme : '';
  }

  getOccupationName(profile: any): string {
    const occ = profile.professional_info?.occupation_id;
    if (!occ) return '';
    if (typeof occ === 'object') return occ.occupation_name || '';
    const found = this.occupations.find(o => o._id === occ);
    return found ? found.occupation_name : '';
  }
}
